import React, {useLayoutEffect, useRef, useState} from 'react';
import GameEndPlayerInfo from "../../GameEndContent/GameEndPlayerInfo/GameEndPlayerInfo";

// Сколько список стоит в порядке прошлого раунда, прежде чем плашки поедут
// на новые места: модалка успевает появиться, и игрок видит, откуда начали
const REORDER_DELAY_MS = 700;
const REORDER_DURATION_MS = 800;

/**
 * Таблица промежуточных итогов раунда. Если известны места прошлого раунда,
 * список сперва показывается в прошлом порядке, а затем плашки плавно
 * переезжают на текущие места (FLIP: DOM сразу в новом порядке, а смещение к
 * прошлому месту задается transform'ом и затем снимается с переходом).
 * Монеты и очки на плашках все время текущие — едут только места
 *
 * @param rows - [{player, standing, previousIndex, previousPlace, finalWinPointsDelta}] в
 * текущем порядке; previousIndex/finalWinPointsDelta — null, если сравнивать
 * не с чем (первый раунд, перезагрузка страницы)
 */
const RoundEndStandings = ({rows}) => {
  const rowElementsRef = useRef({});
  const hasPreviousPlaces = rows.every((row) => typeof row.previousIndex === 'number');
  const hasMoves = hasPreviousPlaces && rows.some((row, index) => row.previousIndex !== index);
  const [isReordered, setIsReordered] = useState(!hasMoves);

  // Раскладка только при появлении модалки: пока она открыта, строки не
  // меняются — слепок итогов раунда фиксирован
  useLayoutEffect(() => {
    if (!hasMoves) {
      return undefined;
    }

    const elements = rows.map((row) => rowElementsRef.current[row.player.id]);
    const tops = elements.map((element) => element.offsetTop);
    elements.forEach((element, index) => {
      const offset = tops[rows[index].previousIndex] - tops[index];
      element.style.transition = 'none';
      element.style.transform = `translateY(${offset}px)`;
      // Поднимающиеся плашки проезжают поверх опускающихся
      element.style.zIndex = offset > 0 ? '1' : '';
    });

    const timer = setTimeout(() => {
      elements.forEach((element) => {
        element.style.transition = `transform ${REORDER_DURATION_MS}ms ease-in-out`;
        element.style.transform = '';
      });
      setIsReordered(true);
    }, REORDER_DELAY_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="round-end-modal-players">
      {rows.map((row, index) => (
        <div
          key={row.player.id}
          className="round-end-modal-player-row"
          ref={(element) => {
            rowElementsRef.current[row.player.id] = element;
          }}
        >
          <GameEndPlayerInfo
            player={{...row.player, finalWinPoints: row.standing.finalWinPoints}}
            // До переезда значок места — прошлый, чтобы не расходиться с
            // позицией плашки в списке
            index={isReordered ? index : row.previousIndex}
            place={isReordered ? (row.standing.place ?? index + 1) : row.previousPlace ?? row.previousIndex + 1}
            isCompactMode={true}
            isBotMarkOnIcon={true}
            coins={row.standing.coins}
            winPoints={row.standing.winPoints}
            // Стрелка появляется, когда плашка уже на новом месте
            placeChange={hasPreviousPlaces && isReordered ? row.previousPlace - (row.standing.place ?? index + 1) : undefined}
            finalWinPointsDelta={row.finalWinPointsDelta ?? undefined}
          />
        </div>
      ))}
    </div>
  );
};

export default RoundEndStandings;
